import { FC, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "mui";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { API_BASE_URL } from "services/authService";
import { logInWithEmail, logInWithExchange } from "store/session/thunks";
import MSLogo from "assets/images/ms-symbol.svg";
import styles from "modules/Auth/components/LogInForm/LoginForm.module.scss";
import { selectIsAuth } from "store/session/selectors";
import { getUserEmail } from "store/session";
import { LoginStep } from "constants/loginSteps";
interface LoginFormProps {
  onSend: (value: LoginStep) => void;
}

const LoginForm: FC<LoginFormProps> = ({ onSend }: LoginFormProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const savedEmail = getUserEmail()?.email;

  const isAuthenticated = useSelector(selectIsAuth);

  const formik = useFormik({
    initialValues: {
      email: savedEmail || "",
      password: "",
      rememberMe: Boolean(savedEmail),
    },
    onSubmit: (values) => {
      dispatch(logInWithEmail(values));
    },
  });
  const handleGoNext = () => {
    if (isAuthenticated) {
      navigate("/");
    }
  };

  const handleLoginMs = () => {
    window.location.replace(`${API_BASE_URL}/microsoftauth/login`);
  };

  // Handle SSO callback success
  const dispatchLogin = () => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");
    if (token !== null) {
      // Get correct auth credentials and redirect to home
      dispatch(logInWithExchange(token));
    }
  };
  useEffect(handleGoNext, [isAuthenticated, navigate]);
  useEffect(dispatchLogin, [dispatch, location]);

  return (
    <Box className={styles.loginForm}>
      <Box className={styles.title}>
        <h1>Welcome</h1>
      </Box>
      <Box className={styles.description}>
        <Typography variant="subtitle1">
          Get started with your account today!
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.fields}>
          <TextField
            className={styles.textInput}
            InputProps={{
              classes: { input: styles.input },
            }}
            label="Email"
            name="email"
            variant="outlined"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <TextField
            className={styles.textInput}
            InputProps={{
              classes: { input: styles.input },
            }}
            label="Password"
            name="password"
            variant="outlined"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <Box className={styles.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formik.values.rememberMe}
                  onChange={formik.handleChange}
                />
              }
              label="Remember Me"
            />
            <Link to={"/"}> Forgot password?</Link>
          </Box>
          <Button
            className={styles.loginButton}
            type="submit"
            variant="contained"
          >
            Log in
          </Button>
          <Box className={styles.divider}>
            <Divider />
            <span>OR</span>
            <Divider />
          </Box>
          <Button
            className={styles.buttonMS}
            startIcon={<img alt="Microsoft Logo" src={MSLogo} />}
            onClick={handleLoginMs}
          >
            Sign in with Microsoft
          </Button>
          <Box className={styles.noAccount}>
            <Typography variant="caption">
              Don't have an account? <Link to={"https://account-d.docusign.com/oauth/auth?response_type=code&redirect_uri=https%3A%2F%2Fdevelopers.docusign.com%2Fauth%2Fdocusign-demo%2Fcallback&scope=manage_app_keys%20signature%20openid%20cors%20click.manage%20click.send%20organization_read%20group_read%20permission_read%20user_read%20user_write%20account_read%20domain_read%20identity_provider_read%20user_data_redact%20dtr.rooms.read%20dtr.rooms.write%20dtr.documents.read%20dtr.documents.write%20dtr.profile.read%20dtr.profile.write%20dtr.company.read%20dtr.company.write%20room_forms%20notary_write%20notary_read%20spring_read%20spring_write&state=https%3A%2F%2Fdevelopers.docusign.com%2F&client_id=f0f27f0e-857d-4a71-a4da-32cecae3a978"}>Create an account</Link>
            </Typography>
            <br></br>
            <Typography variant="caption">
              By signing up, I agree to {"<Name>"}
              <Link to={"/"}>Terms and conditions</Link>
            </Typography>
          </Box>
        </div>
      </form>
    </Box>
  );
};

export default LoginForm;
